import { downloadProductPublicAsset } from 'api/services/ProductsService';
import { optionToValue } from '../api/types/ReferenceData';
import { SegmentTag } from 'types/global';
import { toast } from 'react-toastify';
import { Asset } from 'types/Asset';
import { SearchDocument, SearchFilter } from 'types/Search';
import { translateToMetaDataKey } from './utils';
import { useLogEvent } from 'api/services/LogEventService';
import { LogEventType } from 'types/LogEvents';

export const formatSearchLog = (
  search: {
    results: { document: SearchDocument }[];
    search: { query: string };
  },
  searchFilters?: SearchFilter[],
  segment?: SegmentTag
) => {
  const result = search.results.map((obj, index) => {
    const root = obj.document;
    return {
      productId: root.old_key,
      productName: root.name,
      segment: root.segment,
      supplierId: root.company.key,
      searchParameters: {
        position: obj.document.position ?? index, // when we load the search page we run the function and can use index, but when clicking a product the index doesnt help us know the product position on the table therefore we use the position to log properly the product position when the user clicked on it
        searchQuery: search.search.query,
        searchFilter: generateSearchFilters(searchFilters, segment)
      }
    };
  });

  return result;
};
export const generateSearchFilters = (
  searchFilters?: SearchFilter[],
  segment?: SegmentTag
) => {
  const result: {
    [key: string]: { type: string; value: SegmentTag | string }[];
  } = {};
  result[`metadata.segment_string`] = [
    { type: 'equals', value: segment ? segment : SegmentTag.PERSONALCARE }
  ];
  searchFilters &&
    searchFilters.forEach(filter => {
      // if cannot be translated we should use the original key
      const translated = translateToMetaDataKey(filter.field);
      let fieldString = null;

      if (translated == null) {
        fieldString = filter.field;
      } else {
        fieldString = `metadata.${translated}_${
          filter.field === 'companies' ? 'string' : 'multi_string'
        }`;
      }

      result[fieldString] = result[fieldString]
        ? result[fieldString].concat([
            {
              value: filter.value,
              type: 'equals'
            }
          ])
        : [{ value: filter.value, type: 'equals' }];
    });

  return result;
};

export const toEntityUserProfile = (formObject: object) => {
  const keys = Object.keys(formObject) as Array<keyof typeof formObject>;
  const newObj: any = {};
  keys.forEach(key => {
    const entry = formObject[key];
    if (!entry) return;

    if (typeof entry !== 'object') {
      newObj[key] = entry;
      return;
    }
    //if an object or array then return an array with just value

    if (Array.isArray(entry)) {
      if (
        key !== 'roles' &&
        key !== 'links' &&
        key !== 'media' &&
        key !== 'languages' &&
        key !== 'shipping_address'
      ) {
        const newArr: string[] = [];
        // @ts-ignore
        entry.forEach((e: any) => {
          // if (key === 'languages') {
          //   return newArr.push(e.label)
          // }
          if (e?.value) {
            return newArr.push(e?.value);
          }
        });
        newObj[key] = newArr;
      } else {
        if (key === 'languages') {
          const newArr: any = [];
          // @ts-ignore
          entry.map((e: any) => {
            return newArr.push({ name: e.name.label, level: e.level.value });
          });
          newObj[key] = newArr;
        } else {
          newObj[key] = entry;
        }
      }
    } else {
      newObj[key] = optionToValue(entry);
    }
  });
  //@ts-ignore
  newObj['newsletter'] = formObject.newsletter;
  //@ts-ignore

  newObj['shipping_address'] = formObject.shipping_address;
  // newObj.shipping_address.company = optionToValue(newObj.shipping_address.company)

  newObj.shipping_address.country_code = optionToValue(
    newObj.shipping_address.country
  );

  delete newObj.shipping_address.country;

  if (newObj.shipping_address.company === undefined) {
    newObj.shipping_address.company = '';
  }
  if (newObj.shipping_address.country_code === undefined) {
    newObj.shipping_address.country_code = '';
  }

  return newObj;
};

export const downloadFile = (
  data: any,
  filename: string,
  extension: 'csv' | 'xlsx' = 'csv'
) => {
  const ext =
    extension === 'csv'
      ? 'text/csv'
      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  // Creating a Blob for having a csv/xlsx file format
  // and passing the data with type
  const blob = new Blob([data], {
    type: ext
  });

  // Creating an object for downloading url
  const url = window.URL.createObjectURL(blob);

  // Creating an anchor(a) tag of HTML
  const a = document.createElement('a');

  // Passing the blob downloading url
  a.setAttribute('href', url);

  // Setting the anchor tag attribute for downloading
  // and passing the download file name
  a.setAttribute('download', filename);

  // Performing a download with click
  a.click();
};

export type LogProducts = {
  productId?: string;
  segment?: SegmentTag;
  supplierId?: string;
}[];

export const useDownloadPublicFile = () => {
  const logEvent = useLogEvent();

  const downloadPublicFile = (
    asset: Asset,
    logProducts: LogProducts,
    eventKey?: string
  ) => {
    const handleSuccess = (response: any) => {
      logEvent(logProducts, LogEventType.PRODUCT_DOWNLOAD, eventKey); //TODO: PRODUCT_DOWNLOAD isnt a good name to log download of documentss, BE should imporve this and support PRODUCT_DOCUMENT_DOWNLOAD
      const file = new Blob([response.data], { type: response.data.type });
      const fileURL = URL.createObjectURL(file);

      const ios =
        /iP(ad|od|hone)/i.test(window.navigator.userAgent) ||
        navigator.maxTouchPoints > 0;

      if (!ios) {
        // https://stackoverflow.com/a/70463940/4434686
        // Otherwise the Safari popup blocker blocks it (and normal users don't see it)
        setTimeout(() => {
          window.open(fileURL, '_blank');
        });
      } else {
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = 'filename';
        link.target = '_blank'; // don't open it in the same tab...
        document.body.appendChild(link);

        link.click();

        // Cleanup: Remove the link and revoke the Blob URL
        document.body.removeChild(link);
        URL.revokeObjectURL(fileURL);
      }
    };

    downloadProductPublicAsset(asset, handleSuccess, () =>
      toast.error(`Couldn't download file.`)
    );
  };
  return downloadPublicFile;
};

export function reorderObject(
  keys: string[],
  obj: Record<string, any>
): Record<string, any> {
  const reorderedObj: Record<string, any> = {};

  // Iterate through the keys array and add properties to the reordered object in the specified order
  keys.forEach(key => {
    // eslint-disable-next-line no-prototype-builtins
    if (obj.hasOwnProperty(key)) {
      reorderedObj[key] = obj[key];
    }
  });

  return reorderedObj;
}
